import { useEffect, useState } from 'react';
import { Api } from '../../../logic/Api/Api';
import { globalSession } from '../../../tracing/session';
import { IAccountMigrationStatus } from './models';
import { parseSseEvents } from './parseSseEvents';

export interface IAccountMigrationEventsState
{
	/** Latest run per FoundationId, as last reported by the stream */
	statuses: Record<string, IAccountMigrationStatus>;
	/** True while the stream is open and readable */
	connected: boolean;
	/** Set when the server refused the stream (e.g. 403); no further reconnects are attempted */
	error?: string;
}

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const ENDPOINT = 'supporttools/account-migrations/events';

// Resolves (never rejects) on timeout OR abort so the caller can simply re-check signal.aborted.
function sleep(ms: number, signal: AbortSignal): Promise<void>
{
	return new Promise(resolve =>
	{
		if (signal.aborted)
		{
			resolve();
			return;
		}
		const onAbort = () =>
		{
			clearTimeout(id);
			resolve();
		};
		const id = setTimeout(() =>
		{
			signal.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal.addEventListener('abort', onAbort, { once: true });
	});
}

const isAbortError = (e: unknown) => (e as { name?: string })?.name === 'AbortError';

/** The list is newest-first per account, so the first entry seen for a FoundationId wins. */
function latestPerAccount(list: IAccountMigrationStatus[]): Record<string, IAccountMigrationStatus>
{
	const next: Record<string, IAccountMigrationStatus> = {};
	for (const s of list)
	{
		if (!(s.FoundationId in next))
		{
			next[s.FoundationId] = s;
		}
	}
	return next;
}

/**
 * Subscribes to GET /supporttools/account-migrations/events (text/event-stream) for as long as the
 * component is mounted, reconnecting with exponential backoff (1s → 30s) when the stream drops.
 *
 * The stream is read with `fetch` rather than `EventSource` because the browser `EventSource` cannot
 * send the Authorization / x-masquerade-as headers that `Api` adds.
 *
 * The caller must memoize `api` (e.g. `useMemo(() => new Api(...), [token])`), otherwise the stream
 * is torn down and reopened on every render.
 */
export function useAccountMigrationEvents(api: Api): IAccountMigrationEventsState
{
	const [statuses, setStatuses] = useState<Record<string, IAccountMigrationStatus>>({});
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string>();

	useEffect(() =>
	{
		const controller = new AbortController();
		const { signal } = controller;
		let backoff = MIN_BACKOFF_MS;

		const applyMigrations = (data: string) =>
		{
			let list: IAccountMigrationStatus[];
			try
			{
				list = JSON.parse(data);
			}
			catch (e)
			{
				// A malformed frame should not kill the stream; skip it.
				globalSession.error(String(e));
				return;
			}
			if (!signal.aborted)
			{
				setStatuses(latestPerAccount(Array.isArray(list) ? list : []));
			}
		};

		const handleBuffer = (buffer: string): string =>
		{
			const { events, rest } = parseSseEvents(buffer);
			for (const ev of events)
			{
				if (ev.event === 'migrations')
				{
					applyMigrations(ev.data);
				}
			}
			return rest;
		};

		// One connection attempt. Returns true when the caller should reconnect, false when terminal.
		const connectOnce = async (): Promise<boolean> =>
		{
			// Fresh init per attempt: sendAsync appends `traceparent` to this Headers instance in place,
			// so reusing it across reconnects would accumulate duplicate headers.
			const init = api.authenticatedRequestInit();
			const headers = init.headers as Headers;
			headers.set('Accept', 'text/event-stream');
			headers.delete('Content-Type');

			const response = await api.sendAsync(ENDPOINT, { ...init, method: 'GET', cache: 'no-store', signal });

			if (!response.ok)
			{
				// 403 = no permission, etc. Not transient, so stop trying.
				if (!signal.aborted)
				{
					setConnected(false);
					setError(`Event stream unavailable (${response.status}${response.statusText ? ' ' + response.statusText : ''})`);
				}
				return false;
			}

			if (!response.body)
			{
				throw new Error('Event stream response had no body');
			}

			if (!signal.aborted)
			{
				setConnected(true);
				setError(undefined);
			}

			const reader = response.body.getReader();
			// New decoder and buffer per connection so a half frame from a dropped connection is never
			// prepended to the next stream.
			const decoder = new TextDecoder();
			let buffer = '';
			let gotChunk = false;
			try
			{
				for (;;)
				{
					const { value, done } = await reader.read();
					if (done)
					{
						break;
					}
					if (!gotChunk)
					{
						// The server actually streamed bytes: treat as a successful connection.
						gotChunk = true;
						backoff = MIN_BACKOFF_MS;
					}
					buffer += decoder.decode(value, { stream: true });
					buffer = handleBuffer(buffer);
				}
				// Flush any bytes the decoder is still holding.
				buffer += decoder.decode();
				handleBuffer(buffer);
			}
			finally
			{
				reader.releaseLock();
			}

			// Server closed the stream cleanly; reconnect.
			return true;
		};

		(async () =>
		{
			while (!signal.aborted)
			{
				let retry = true;
				try
				{
					retry = await connectOnce();
				}
				catch (e)
				{
					if (signal.aborted || isAbortError(e))
					{
						// Unmount or api change: nothing to report.
						return;
					}
					// Network drop (TypeError 'Failed to fetch'), missing body, etc. Back off and retry.
					globalSession.error(String(e));
				}

				if (!retry)
				{
					return;
				}

				if (!signal.aborted)
				{
					setConnected(false);
				}
				await sleep(backoff, signal);
				backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
			}
		})();

		return () =>
		{
			controller.abort();
		};
	}, [api]);

	return { statuses, connected, error };
}
